import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Overflow,
  OverflowItem,
  tokens,
  useOverflowMenu,
  makeStyles,
  Tooltip,
} from '@fluentui/react-components';
import { Tag } from '@fluentui/react-tags';
import { RULE_SEPERATOR } from './helpers/constants';
import { extractTargetsFromViewModel } from './helpers/utils';

/**
 * There's a known bug that fluentui/react-overflow doesn't take gap of the overflow container
 * and margin of overflow item into account and cause weird styling issues.
 * https://github.com/microsoft/fluentui/issues/23066
 */
const useUrlRulesPreviewStyles = makeStyles({
  overflowItem: {
    marginBottom: tokens.spacingVerticalXS,
  },
  overflowCount: {
    marginLeft: tokens.spacingHorizontalXS,
  },
  tag: {
    marginRight: tokens.spacingHorizontalXS,
  },
});

const MIN_VISIBLE_ROWS = 3;
const MAX_TAG_LENGTH = 35;

const OverflowPlaceholder = () => {
  const styles = useUrlRulesPreviewStyles();
  const { ref, overflowCount, isOverflowing } = useOverflowMenu();
  if (!isOverflowing) {
    return null;
  }

  return (<span className={styles.overflowCount} ref={ref}>+{overflowCount}</span>);
};

export const UrlRulesPreview = (props) => {
  const { urlRules, isSingleRow } = props;

  const styles = useUrlRulesPreviewStyles();

  const { urlTargets } = useMemo(() => extractTargetsFromViewModel(urlRules), [urlRules]);

  const truncateWithEllipsis = useCallback((str) => {
    const maxLength = isSingleRow ? MAX_TAG_LENGTH : MAX_TAG_LENGTH * 2;
    return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str;
  }, [isSingleRow]);

  const renderPreview = useCallback(() => {
    return (
      <Overflow overflowAxis="vertical" minimumVisible={MIN_VISIBLE_ROWS}>
        <div key={JSON.stringify(urlTargets)}>
          { urlTargets.map((target, index) => {
              const targetString = target.join(RULE_SEPERATOR);
              return (
                <OverflowItem key={targetString} id={targetString}>
                  <div className={styles.overflowItem}>
                    <Tooltip content={targetString}>
                      <Tag>{truncateWithEllipsis(targetString)}</Tag>
                    </Tooltip>
                    { (index === MIN_VISIBLE_ROWS - 1) && <OverflowPlaceholder /> }
                  </div>
                </OverflowItem>
              );
            }) }
        </div>
      </Overflow>
    );
  }, [styles, urlTargets]);

  const renderPreviewRow = useCallback(() => (
    <Overflow>
      <div>
        { urlTargets.map((target) => {
          const targetString = target.join(RULE_SEPERATOR);
          return (
            <OverflowItem key={targetString} id={targetString}>
              <Tooltip content={targetString}>
                <Tag className={styles.tag}>{truncateWithEllipsis(targetString)}</Tag>
              </Tooltip>
            </OverflowItem>
          );
        }) }
        <OverflowPlaceholder />
      </div>
    </Overflow>
  ), [styles, urlTargets]);

  return (
    <div>
      { isSingleRow ? renderPreviewRow() : renderPreview() }
    </div>
  );
};

UrlRulesPreview.propTypes = {
  urlRules: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isSingleRow: PropTypes.bool,
};


UrlRulesPreview.defaultProps = {
  isSingleRow: false,
};
